import { useSearchParams } from "react-router-dom";
import { DASHBOARD_SURVEY_LIST_PAGE_SIZE, FILTER_STATUS_LIST, FilterStatus } from "./dashboard-survey-list.constants";
import { useDeleteSurveyMutation, useGetSurveysQuery } from "../../../redux/survey/survey.endpoints";
import { FetchSurveyData } from "../../../services/surveys/survey.types";
import { useEffect } from "react";

export interface UseDashboardSurveyListReturn {
  readonly activePage: number;
  readonly activeStatus?: FilterStatus;
  readonly filteredResults: boolean;
  readonly showAllUsers?: boolean;
  readonly onFilterSelected: (option: string) => void;
  readonly data?: FetchSurveyData;
  readonly isFetching: boolean;
  readonly onPageChanged: (page: number) => void;
  readonly onSurveyDeletion: (extSurveyId: string) => void;
  readonly onTitleSearch: (title: string) => void;
  readonly onUserFilterSelected: (showAllUsers: boolean) => void;
}

export const isValidStatusParam = (status: string): status is FilterStatus => {
  return FILTER_STATUS_LIST.includes(status as FilterStatus);
};

export const useDashboardSurveyList = (): UseDashboardSurveyListReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status') ?? '';
  const titleParam = searchParams.get('title') ?? '';
  const allUsersParam = searchParams.get('allUsers') ?? '';
  const activeStatus = isValidStatusParam(statusParam) ? statusParam : undefined;
  const pageParam = searchParams.get('page') ?? '1';
  const activePage = parseInt(pageParam);
  const showAllUsers = allUsersParam === 'true';
  const { data, isFetching, refetch } = useGetSurveysQuery({
    page: activePage,
    survey_status: activeStatus ?? FILTER_STATUS_LIST.join(','),
    page_size: DASHBOARD_SURVEY_LIST_PAGE_SIZE,
    title: titleParam,
    user: !showAllUsers,
  });
  const [deleteSurvey, { isSuccess: deleteSuccess }] = useDeleteSurveyMutation();

  const filteredResults = Boolean(activeStatus || titleParam);

  useEffect(() => {
    if (!deleteSuccess) {
      return;
    }
    const currentPageLength = data?.surveys.length ?? 0;
    const shouldGoBack = currentPageLength - 1 <= 0 && activePage > 1;
    if (shouldGoBack) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', (activePage - 1).toString());
      setSearchParams(newSearchParams);
    } else {
      refetch();
    }
  }, [deleteSuccess]);

  const onFilterSelected = (option: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (isValidStatusParam(option)) {
      if (option !== statusParam) {
        newSearchParams.set('status', option);
        newSearchParams.delete('page');
        setSearchParams(newSearchParams);
      }
    } else {
      if (statusParam) {
        newSearchParams.delete('status');
        newSearchParams.delete('page');
        setSearchParams(newSearchParams);
      }
    }
  };

  const onUserFilterSelected = (showAllUsers: boolean) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (showAllUsers) {
      newSearchParams.set('allUsers', 'true');
    } else {
      newSearchParams.delete('allUsers');
    }
    newSearchParams.delete('page');
    setSearchParams(newSearchParams);
  };

  const onPageChanged = (page: number) => {
    if (page !== activePage) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', page.toString());
      setSearchParams(newSearchParams);
    }
  };

  const onTitleSearch = (title: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (title) {
      newSearchParams.set('title', title);
    } else {
      newSearchParams.delete('title');
    }
    newSearchParams.delete('page');
    setSearchParams(newSearchParams);
  };

  const onSurveyDeletion = (extSurveyId: string) => {
    void deleteSurvey({ ext_survey_id: extSurveyId });
  };

  return {
    activePage,
    activeStatus,
    filteredResults,
    showAllUsers,
    onFilterSelected,
    data,
    isFetching,
    onPageChanged,
    onSurveyDeletion,
    onTitleSearch,
    onUserFilterSelected,
  };
};
